/**
 * IL TETTO GIORNALIERO DELLE FUNZIONI AI, PER AZIENDA (22/10/2026).
 *
 * ## Perché
 * Simone, 22/10/2026: Yuma «potrebbe essere comunque disponibile anche per gli account gratuiti, con un tetto massimo di
 * richieste, in modo che noi evitiamo di spendere troppo» (confermato: «Sì, confermo, procedi»). Il 20/10 aveva già
 * approvato, per l'accesso anticipato, 1 € al giorno per azienda, regolabile da lui dalla console e MAI mostrato in euro
 * agli utenti. Yuma entra quindi nel piano gratuito (e per l'inclusione gerarchica anche in Base e Pro), e ogni azienda
 * che NON ha la capability `ai_senza_limite_giornaliero` (piano Business) ha un tetto di spesa al giorno.
 *
 * ## Cosa NON è
 * Non è la fatturazione a consumo (`strumenti/consumi.ts`, mensile, con tetto scelto dall'azienda e addebito su Stripe):
 * è una protezione della piattaforma. Il costo che conta è quello VERO del fornitore (token × listino dei modelli),
 * senza ricarico; nessuno lo vede tranne il super utente, nessuno lo paga.
 *
 * ## Il dato
 * `platform_costi/tetto_ai_giornaliero` (lo scrive solo il server, dalla console del super utente: regola cardine #4):
 *  - `eur_per_usd`: il cambio con cui il costo in dollari diventa euro;
 *  - `per_piano`: il tetto in CENTESIMI di euro per i piani che lo hanno (`free`, `base`, `pro`);
 *  - `eccezioni`: il tetto di una singola azienda, quando Simone decide diversamente dal suo piano.
 * La spesa del giorno sta in `consumi_ai_giornalieri/{azienda}_{giorno}` (giorno civile nel fuso dell'azienda), in
 * MICRODOLLARI interi: niente virgola mobile che si accumula, e un solo arrotondamento quando si confronta.
 *
 * PURO: niente Firestore, niente import. Lo usano la funzione di Yuma (prima e dopo la chiamata al modello), la console e
 * le prove.
 */

/** La capability che toglie il tetto: sta nel piano Business (`seed-tier-definitions.cjs`). */
export const CAPABILITY_SENZA_LIMITE_GIORNALIERO = 'ai_senza_limite_giornaliero';

export const COLLEZIONE_CONFIG_TETTO_AI = 'platform_costi';
export const DOC_CONFIG_TETTO_AI = 'tetto_ai_giornaliero';
export const COLLEZIONE_SPESA_AI_GIORNALIERA = 'consumi_ai_giornalieri';

/** I piani che possono avere un tetto. Il Business non c'è: ha la capability che lo toglie. */
export const PIANI_COL_TETTO = ['free', 'base', 'pro'] as const;
export type PianoColTetto = (typeof PIANI_COL_TETTO)[number];

export interface ConfigTettoAi {
  schema: 1;
  /** Il cambio: 1 dollaro = `eur_per_usd` euro. */
  eur_per_usd: number;
  /** Il tetto in centesimi di euro per piano. Un piano assente non è configurato (si ferma, e lo si dice). */
  per_piano: Partial<Record<PianoColTetto, number>>;
  /** Il tetto in centesimi di euro di una singola azienda: vince sul piano. */
  eccezioni: Record<string, number>;
}

const eOggetto = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const centesimiValidi = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 100_000;

/** Pura: la configurazione validata, oppure i motivi. Nessun valore di ripiego. */
export function validaConfigTettoAi(dati: unknown): { ok: true; valore: ConfigTettoAi } | { ok: false; motivi: string[] } {
  if (!eOggetto(dati)) return { ok: false, motivi: ['manca il documento'] };
  const motivi: string[] = [];
  if (dati.schema !== 1) motivi.push('schema: serve 1');
  const cambio = dati.eur_per_usd;
  if (typeof cambio !== 'number' || !Number.isFinite(cambio) || cambio <= 0 || cambio > 10) motivi.push('eur_per_usd: serve un numero fra 0 e 10');
  const per_piano: Partial<Record<PianoColTetto, number>> = {};
  if (!eOggetto(dati.per_piano)) motivi.push('per_piano: serve un oggetto');
  else {
    for (const [piano, v] of Object.entries(dati.per_piano)) {
      if (!(PIANI_COL_TETTO as readonly string[]).includes(piano)) motivi.push(`per_piano.${piano}: piano senza tetto`);
      else if (!centesimiValidi(v)) motivi.push(`per_piano.${piano}: servono centesimi interi da 0 a 100.000`);
      else per_piano[piano as PianoColTetto] = v;
    }
  }
  const eccezioni: Record<string, number> = {};
  if (dati.eccezioni !== undefined && !eOggetto(dati.eccezioni)) motivi.push('eccezioni: serve un oggetto');
  else if (eOggetto(dati.eccezioni)) {
    for (const [azienda, v] of Object.entries(dati.eccezioni)) {
      if (!/^[A-Za-z0-9_-]{1,64}$/.test(azienda)) motivi.push(`eccezioni.${azienda}: id azienda non valido`);
      else if (!centesimiValidi(v)) motivi.push(`eccezioni.${azienda}: servono centesimi interi da 0 a 100.000`);
      else eccezioni[azienda] = v;
    }
  }
  if (motivi.length) return { ok: false, motivi };
  return { ok: true, valore: { schema: 1, eur_per_usd: cambio as number, per_piano, eccezioni } };
}

/** Il tetto dell'azienda in centesimi di euro: l'eccezione, altrimenti quello del suo piano; `null` = non configurato. */
export function tettoInCentesimi(cfg: ConfigTettoAi, a: { tenantId: string; piano: string }): number | null {
  if (Object.prototype.hasOwnProperty.call(cfg.eccezioni, a.tenantId)) return cfg.eccezioni[a.tenantId];
  const delPiano = (PIANI_COL_TETTO as readonly string[]).includes(a.piano) ? cfg.per_piano[a.piano as PianoColTetto] : undefined;
  return typeof delPiano === 'number' ? delPiano : null;
}

/** Dollari → microdollari interi, per eccesso: la spesa non si sottostima mai. */
export function microusdDaUsd(usd: number): number {
  if (!Number.isFinite(usd) || usd <= 0) return 0;
  return Math.ceil(usd * 1_000_000 - 1e-6);
}

/** Microdollari spesi → centesimi di euro, per eccesso (un solo arrotondamento). */
export function centesimiDaMicrousd(microusd: number, eurPerUsd: number): number {
  if (microusd <= 0) return 0;
  return Math.ceil((microusd * eurPerUsd) / 10_000 - 1e-9);
}

/** Valori di enum: chiavi, mai tradotte. */
export type MotivoTettoAi = 'tetto_raggiunto' | 'tetto_non_configurato';

/** Si può chiamare il modello? Sotto il tetto sì; al tetto o oltre no. */
export function decidiTettoAi(a: { tettoCentesimi: number | null; spesiCentesimi: number }): { consentito: true } | { consentito: false; motivo: MotivoTettoAi } {
  if (a.tettoCentesimi === null) return { consentito: false, motivo: 'tetto_non_configurato' };
  if (a.spesiCentesimi >= a.tettoCentesimi) return { consentito: false, motivo: 'tetto_raggiunto' };
  return { consentito: true };
}

/** L'id del documento della spesa di un'azienda in un giorno civile ('AAAA-MM-GG'). */
export function idSpesaAiGiornaliera(tenantId: string, giorno: string): string {
  return `${tenantId}_${giorno}`;
}

/** La chiave di una voce di spesa: una per chiamata al modello, `[A-Za-z0-9_-]`. */
export function chiaveVoceValida(chiave: string): boolean {
  return /^[A-Za-z0-9_-]{8,120}$/.test(chiave);
}
